/** @format */

import { Grid } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { GA4_SEARCH_LOCATION } from 'src/analytics/searchGa4';
import { buildSearchNoResultsParams, buildSearchParams, buildSearchResultClickParams } from 'src/analytics/buildGa4EventPayload';
import useGa4Analytics from 'src/hooks/useGa4Analytics';
import { Button, KeywordSearchInput } from 'src/components';
import Breadcrumb from 'src/components/Breadcrumb';
import Page from 'src/components/Page';
import {
  getSearchResults,
  getTopicOptions,
  setSelectedBrand,
  setSelectedKeywords,
  setSelectedLanguage,
  setSelectedSortBy,
  setSelectedTopicId
} from 'src/slices/search';
import { useDispatch, useSelector } from 'src/store';
import { buildSearchUrlSearch, parseSearchUrlSearch } from 'src/utils/searchUrl';
import SearchFilters from './SearchFilters';
import { ContentItem, ContentListSkeleton } from '../extranet/contentItem';

export default function SearchPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const pageTitle = t('common.search');
  const {
    selectedBrand,
    selectedTopicId,
    selectedLanguage,
    selectedSortBy,
    selectedKeywords,
    results,
    resultsNextCursor,
    resultsTotal,
    resultsLoading,
    resultsAppending,
    resultsError
  } = useSelector(state => state.search);

  const { trackSearch, trackSearchNoResults, trackSearchResultClick } = useGa4Analytics();
  const searchFetchStateRef = useRef({ loading: false, appending: false });

  useEffect(() => {
    dispatch(getTopicOptions(selectedLanguage));
  }, [dispatch, selectedLanguage]);

  useEffect(() => {
    const parsed = parseSearchUrlSearch(location.search);

    if (parsed.brand !== selectedBrand) {
      dispatch(setSelectedBrand(parsed.brand));
    }
    if (parsed.topicId !== selectedTopicId) {
      dispatch(setSelectedTopicId(parsed.topicId));
    }
    if (parsed.language !== selectedLanguage) {
      dispatch(setSelectedLanguage(parsed.language));
    }
    if (parsed.sortBy !== selectedSortBy) {
      dispatch(setSelectedSortBy(parsed.sortBy));
    }

    const currentKeywordsSorted = [...selectedKeywords].sort().join(',');
    const urlKeywordsSorted = [...parsed.keywords].sort().join(',');
    if (currentKeywordsSorted !== urlKeywordsSorted) {
      dispatch(setSelectedKeywords(parsed.keywords));
    }
  }, [dispatch, location.search, selectedBrand, selectedLanguage, selectedSortBy, selectedTopicId, selectedKeywords]);

  useEffect(() => {
    dispatch(getSearchResults());
  }, [dispatch, selectedBrand, selectedLanguage, selectedSortBy, selectedTopicId, selectedKeywords]);

  useEffect(() => {
    const { loading: wasLoading, appending: wasAppending } = searchFetchStateRef.current;

    searchFetchStateRef.current = {
      loading: resultsLoading,
      appending: resultsAppending
    };

    if (!wasLoading || resultsLoading || wasAppending || resultsAppending || resultsError) {
      return;
    }

    const searchTerm = selectedKeywords.filter(Boolean).join(' ');
    const searchLocation = GA4_SEARCH_LOCATION.SEARCH_PAGE;

    trackSearch(
      buildSearchParams({
        search_term: searchTerm || undefined,
        results_count: resultsTotal,
        search_location: searchLocation
      })
    );

    if (resultsTotal === 0) {
      trackSearchNoResults(
        buildSearchNoResultsParams({
          search_term: searchTerm || undefined,
          search_location: searchLocation
        })
      );
    }
  }, [
    resultsLoading,
    resultsAppending,
    resultsTotal,
    resultsError,
    selectedKeywords,
    trackSearch,
    trackSearchNoResults
  ]);

  useEffect(() => {
    return () => {
      dispatch(setSelectedKeywords([]));
    };
  }, [dispatch]);

  const replaceSearchUrl = updates => {
    const nextState = {
      brand: selectedBrand,
      topicId: selectedTopicId,
      language: selectedLanguage,
      sortBy: selectedSortBy,
      keywords: selectedKeywords,
      ...updates
    };

    navigate({
      pathname: location.pathname,
      search: buildSearchUrlSearch(nextState)
    }, { replace: true });
  };

  const handleKeywordsChange = keywords => {
    replaceSearchUrl({ keywords });
  };

  const handleSearchResultClick = useCallback(
    (item, resultIndex) => {
      if (item?.id == null) {
        return;
      }

      const searchTerm = selectedKeywords.filter(Boolean).join(' ');

      trackSearchResultClick(
        buildSearchResultClickParams({
          search_term: searchTerm || undefined,
          search_location: GA4_SEARCH_LOCATION.SEARCH_PAGE,
          content_id: String(item.id),
          result_position: resultIndex + 1
        })
      );

      navigate(`/extranet/content/${item.id}/${selectedLanguage}`);
    },
    [navigate, selectedKeywords, selectedLanguage, trackSearchResultClick]
  );

  const hasResults = Array.isArray(results) && results.length > 0;
  const shouldShowResultsSkeleton = resultsLoading && !hasResults;
  const shouldDimResults = resultsLoading && hasResults;

  return (
    <Page className="archive-page search-page" title={pageTitle}>
      <Breadcrumb data={[{ title: pageTitle }]} additionalClasses="search-page__breadcrumb container--narrow" />
      <Grid className="archive-page-container container container--narrow search-page__inner">
        <div className="search-page__query">
          <KeywordSearchInput
            placeholder={t('searchPage.searchPlaceholder')}
            initialKeywords={selectedKeywords}
            onKeywordsChange={handleKeywordsChange}
          />
        </div>

        <SearchFilters />

        <p className="search-page__results-count">{t('searchPage.resultsCount', { count: resultsTotal })}</p>

        <div className={`search-page__list${shouldDimResults ? ' search-page__list--dimmed' : ''}`}>
          {shouldShowResultsSkeleton ? (
            <ContentListSkeleton count={3} />
          ) : (
            results.map((item, index) => (
              <ContentItem item={item} key={item.id} onClick={() => handleSearchResultClick(item, index)} />
            ))
          )}
        </div>

        {resultsNextCursor ? (
          <div className="search-page__load-more-wrap">
            <Button
              type="button"
              text
              disabled={resultsLoading || resultsAppending}
              onClick={() => dispatch(getSearchResults({ append: true, cursor: resultsNextCursor }))}
              additionalClassName="search-page__load-more">
              {t('common.button.loadMore')}
            </Button>
          </div>
        ) : null}
      </Grid>
    </Page>
  );
}
